import { Link } from "react-router-dom";
import {
  CalendarCheck2,
  ArrowRight,
  ShieldCheck,
  Sparkles,
  Bus,
  Award,
  School,
  UsersRound,
  ClipboardCheck,
  MailCheck,
  Building2,
  Heart,
} from "lucide-react";

import SectionHeading from "@/components/SectionHeading";
import ServiceCard from "@/components/ServiceCard";
import BenefitCard from "@/components/BenefitCard";
import TestimonialCard from "@/components/TestimonialCard";
import CTASection from "@/components/CTASection";
import ImagePlaceholder from "@/components/ImagePlaceholder";

import { services } from "@/data/services";
import { schoolBenefits } from "@/data/benefits";
import { testimonials } from "@/data/testimonials";
import { images } from "@/data/business";

const TRUST_ITEMS = [
  { icon: Heart, label: "Child-friendly dental care" },
  { icon: Bus, label: "Fully equipped mobile clinic" },
  { icon: School, label: "Convenient school visits" },
  { icon: Award, label: "Qualified dental professionals" },
];

const STEPS = [
  {
    n: "01",
    title: "The school books a visit",
    body: "A staff member gets in touch and we work with you to find dates that suit your school calendar.",
  },
  {
    n: "02",
    title: "Parents receive information and consent forms",
    body: "We provide clear, plain-English information packs and consent forms for families.",
  },
  {
    n: "03",
    title: "The mobile dental clinic visits the school",
    body: "Our van arrives and children are seen individually in a private, comfortable setting.",
  },
  {
    n: "04",
    title: "Families receive results and follow-up recommendations",
    body: "Every family gets a personalised report and, if needed, a referral for further care.",
  },
];

const Home = () => {
  return (
    <>
      {/* Set page title */}
      <SEO />

      {/* HERO */}
      <section className="relative overflow-hidden blob-bg" data-testid="home-hero">
        <div className="container-page pt-14 pb-20 md:pt-20 md:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 rise-in">
            <span className="eyebrow" data-testid="hero-eyebrow">
              <Sparkles size={14} strokeWidth={2.5} /> Mobile dental care, Australia-wide
            </span>
            <h1 className="mt-5 font-display font-extrabold text-4xl sm:text-5xl lg:text-[3.8rem] leading-[1.05] tracking-tight text-[#14324A]">
              Healthy smiles delivered <span className="text-[#1677C8]">directly to your school</span>
            </h1>
            <p className="mt-6 text-base md:text-lg text-[#4B6B86] leading-relaxed max-w-xl">
              School Smiles Australia provides convenient, child-friendly dental care through a fully equipped mobile dental clinic. We work with schools and families to make preventive dental care accessible, comfortable and stress-free.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link to="/for-schools#enquiry" className="btn-accent" data-testid="hero-book-visit-btn">
                <CalendarCheck2 size={18} strokeWidth={2.5} />
                Book a School Visit
              </Link>
              <Link to="/for-parents" className="btn-outline" data-testid="hero-parent-info-btn">
                Parent Information
                <ArrowRight size={18} strokeWidth={2.5} />
              </Link>
            </div>

            {/* Trust panel */}
            <ul className="mt-10 grid grid-cols-2 gap-3" aria-label="Why families trust us" data-testid="hero-trust-panel">
              {TRUST_ITEMS.map(({ icon: Icon, label }) => (
                <li key={label} className="flex items-center gap-3 bg-white/70 backdrop-blur border border-[#E5F0FA] rounded-full pl-3 pr-4 py-2 text-sm font-medium text-[#14324A]">
                  <span className="w-8 h-8 rounded-full bg-[#F4F9FF] inline-flex items-center justify-center text-[#1677C8]">
                    <Icon size={16} strokeWidth={2.5} />
                  </span>
                  {label}
                </li>
              ))}
            </ul>
          </div>

          {/* Hero visual */}
          <div className="lg:col-span-6 relative">
            <div className="relative grid grid-cols-2 gap-4">
              <div className="rounded-[2rem] overflow-hidden shadow-[0_30px_60px_rgba(20,50,74,0.15)] aspect-[3/4] row-span-2">
                <img
                  src={images.heroChildren}
                  alt="Smiling Australian primary-school children"
                  loading="eager"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="rounded-[2rem] overflow-hidden shadow-[0_20px_40px_rgba(20,50,74,0.12)] aspect-[4/3]">
                <img
                  src={images.friendlyDentist}
                  alt="Friendly dentist with a young patient"
                  loading="eager"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="rounded-[2rem] p-6 bg-[#14324A] text-white flex flex-col justify-between shadow-[0_20px_40px_rgba(20,50,74,0.2)] aspect-[4/3]">
                <ShieldCheck size={28} className="text-[#FFD45C]" strokeWidth={2.5} />
                <div>
                  <div className="font-display font-bold text-3xl">100%</div>
                  <p className="text-white/80 text-sm mt-1">Child-focused, consent-based dental care</p>
                </div>
              </div>
            </div>
            <div className="mt-4 rounded-[2rem] overflow-hidden shadow-[0_20px_40px_rgba(20,50,74,0.12)] aspect-[16/6] van-idle">
              <img
                src={images.dentalVan}
                alt="Modern mobile dental clinic van"
                loading="eager"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="container-page py-20 md:py-28" data-testid="home-intro">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <SectionHeading
              eyebrow="Why us"
              title="Making dental care easier for schools and families"
            />
          </div>
          <div className="lg:col-span-7 text-[#4B6B86] text-base md:text-lg leading-relaxed space-y-5">
            <p>
              School Smiles Australia brings dental services directly to participating schools — reducing travel, missed appointments and disruption for busy families. Everything happens on-site, in a purpose-built environment designed to feel calm and welcoming for children.
            </p>
            <p>
              Our clinicians take the time to explain what we do, answer questions and build trust with young patients. Parents receive clear reports, and schools receive a simple, well-communicated process from start to finish.
            </p>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="container-page py-16 md:py-24" data-testid="home-services">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <SectionHeading
            eyebrow="Our services"
            title="Preventive dental care, delivered on site"
            description="Every service is designed around comfort, safety and clear communication with families."
          />
          <Link to="/services" className="btn-outline w-fit" data-testid="home-services-cta">
            View all services
            <ArrowRight size={18} strokeWidth={2.5} />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s) => (
            <ServiceCard
              key={s.slug}
              icon={s.icon}
              title={s.title}
              description={s.short}
              accent={s.accent}
              testId={`home-service-${s.slug}`}
            />
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="container-page py-16 md:py-24" data-testid="home-how-it-works">
        <SectionHeading
          eyebrow="How it works"
          title="From first enquiry to follow-up, we handle the details"
        />
        <ol className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {STEPS.map((step, idx) => (
            <li key={step.n} className="card-soft p-8 relative" data-testid={`home-step-${idx + 1}`}>
              <div className="text-5xl font-display font-extrabold text-[#1677C8]/15 leading-none">
                {step.n}
              </div>
              <h3 className="mt-2 font-display font-semibold text-lg text-[#14324A]">
                {step.title}
              </h3>
              <p className="mt-3 text-sm text-[#4B6B86] leading-relaxed">{step.body}</p>
              {idx < STEPS.length - 1 && (
                <ArrowRight
                  size={22}
                  className="hidden lg:block absolute top-1/2 -right-5 text-[#1677C8]"
                  strokeWidth={2.5}
                  aria-hidden="true"
                />
              )}
            </li>
          ))}
        </ol>
      </section>

      {/* SCHOOL BENEFITS */}
      <section className="bg-[#F4F9FF] py-20 md:py-28 mt-8" data-testid="home-benefits">
        <div className="container-page">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-4 lg:sticky lg:top-28">
              <SectionHeading
                eyebrow="For schools"
                title="A partner your team can rely on"
                description="Everything schools tell us they need — from admin support to clear parent communication."
              />
              <Link to="/for-schools" className="btn-primary mt-6" data-testid="home-benefits-cta">
                Register your school
                <ArrowRight size={18} strokeWidth={2.5} />
              </Link>
            </div>
            <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-6">
              {schoolBenefits.map((b, i) => (
                <BenefitCard key={b.title} icon={b.icon} title={b.title} body={b.body} testId={`home-benefit-${i}`} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* DENTAL VAN FEATURE */}
      <section className="container-page py-20 md:py-28" data-testid="home-dental-van">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <SectionHeading
              eyebrow="Our dental van"
              title="A clean, private and comfortable clinic on wheels"
              description="Our mobile clinic is designed to bring a warm, calming clinical environment right to your school car park — with proper privacy, climate control and infection-control practices."
            />
            <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm text-[#14324A]">
              {["Private treatment area", "Dedicated sterilisation zone", "Climate control", "Accessible entry"].map((x) => (
                <li key={x} className="flex items-center gap-2">
                  <ShieldCheck size={18} className="text-[#1677C8]" strokeWidth={2.5} />
                  {x}
                </li>
              ))}
            </ul>
            <Link to="/dental-van" className="btn-primary mt-8" data-testid="home-van-cta">
              Explore Our Dental Van
              <ArrowRight size={18} strokeWidth={2.5} />
            </Link>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <ImagePlaceholder
              src={images.dentalVan}
              alt="School Smiles Australia mobile dental van"
              ratio="aspect-[16/9]"
              testId="home-van-image"
            />
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <div className="container-page">
        <CTASection testIdPrefix="home-final-cta" />
      </div>
    </>
  );
};

// Tiny inline helper so we don't need a router-level head manager
const SEO = () => {
  if (typeof document !== "undefined") {
    document.title = "School Smiles Australia — Mobile dental care for schools";
    const desc = "Child-friendly mobile dental care delivered directly to Australian schools. Book a visit for check-ups, cleans, fluoride, sealants and oral-health education.";
    let m = document.querySelector('meta[name="description"]');
    if (!m) {
      m = document.createElement("meta");
      m.setAttribute("name", "description");
      document.head.appendChild(m);
    }
    m.setAttribute("content", desc);
  }
  return null;
};

export default Home;
